import { SvgSprite } from "@skv/core/src/components";
import classnames from "classnames";
import { Dispatch, forwardRef, SetStateAction, useState } from "react";
import {
  Alert as BS_ALERT,
  AlertProps as BS_ALERT_PROPS,
} from "react-bootstrap";

export type AlertProps = Omit<BS_ALERT_PROPS, "ref"> & {
  ref?: React.Ref<HTMLDivElement>;
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    { closeLabel = "Close", transition = true, variant = "danger", ...props },
    ref
  ) => {
    const [show, setShow] = useState(props.show);

    // Ensure local state is in sync with the parent component
    if (props.show !== show) setShow(props.show);

    const handleClose = () => {
      // Hides the alert
      setShow(false);
      // Triggers onClose callback method to notify the parent component
      props.onClose && props.onClose(null, null);
    };

    return (
      <BS_ALERT
        {...props}
        ref={ref}
        show={show || false}
        onClose={props.onClose}
        variant={variant}
        className={classnames(props.className, "d-flex")}
        dismissible={false}
      >
        <IconFromVariant variant={variant} />

        {/* Wrapping tag to ensure BS_ALERT 'd-flex' doesn't apply to props.children nodes */}
        <div>{props.children}</div>

        {props.dismissible && (
          <button
            type="button"
            className={`btn btn-close btn-close-${variant} ml-auto p-3 m-n3`}
            aria-label={closeLabel}
            onClick={handleClose}
          >
            <SvgSprite
              id="glyph:close"
              width={14}
              height={14}
              aria-label="Close"
            />
          </button>
        )}
      </BS_ALERT>
    );
  }
);

interface IconFromVariantProps {
  variant: string;
  size?: number;
  className?: string;
}

export const IconFromVariant = (props: IconFromVariantProps) => {
  const getIconName = (variant: string) => {
    switch (variant) {
      case "primary":
        return "promote";
      case "success":
        return "positive";
      case "danger":
        return "error";
      case "warning":
        return "caution";
      case "info":
        return "info";
      default:
        return null;
    }
  };

  const iconName = getIconName(props.variant);
  if (iconName === null) return <></>;

  return (
    <SvgSprite
      id={`glyph:${iconName}`}
      width={props.size ?? 24}
      height={props.size ?? 24}
      className={classnames("flex-shrink-0", props.className)}
    />
  );
};

export function useAlertState(
  initialState: AlertProps | (() => AlertProps)
): [AlertProps, Dispatch<SetStateAction<AlertProps>>, () => void] {
  const [alert, setAlert] = useState(initialState);
  const hideAlert = () => setAlert({ ...alert, show: false });

  return [alert, setAlert, hideAlert];
}
